export const COLORS = {
  exemplary: '#0A4CFF', // 90–100
  balanced:  '#4FC3F7', // 70–89
  needs:     '#FFD54F', // 50–69
  risk:      '#E57373', // <50
}

export const band = s => s>=90?'exemplary':s>=70?'balanced':s>=50?'needs':'risk'
export const bandLabel = s => s>=90?'Exemplary':s>=70?'Balanced':s>=50?'Needs improvement':'At risk'

// Portfolio scores (RiverSafe)
export const FARMS = [
  { code: 'X10AI_FT_040', score: 92 }, // Simon & John Jones
  { code: 'X10AI_FT_020', score: 87 }, // Tim Jones
  { code: 'X10AI_FT_050', score: 83 }, // Ian Harley
  { code: 'X10AI_FT_010', score: 78 }, // Steve Dakin
  { code: 'X10AI_FT_060', score: 70 }, // Melvin Jones (updated)
  { code: 'X10AI_FT_030', score: 55 }, // Apley Farms
]

// Detailed component data for all six farms
export const FARM_COMPONENTS = {
  // 040 — high performer
  X10AI_FT_040: [
    { key: 'Nutrient Balance', weight: 40, score: 95,
      explain: 'N use closely matches grass uptake; minimal surplus.',
      reasons: ['Optimised fertiliser splits', 'High grass utilisation', 'No long-term surplus in balance'],
      recs: ['Maintain monthly balance review', 'Use weather windows for slurry'] },
    { key: 'Soil Condition', weight: 25, score: 90,
      explain: 'pH in target; P index stable or decreasing.',
      reasons: ['P index 2–3 trending stable', 'Targeted liming', 'Good sward structure'],
      recs: ['Spot-lime low pH fields', 'Track OM annually'] },
    { key: 'Runoff Protection', weight: 20, score: 92,
      explain: 'Buffers and layout minimise overland flow.',
      reasons: ['5–10 m margins on risk fields', 'Yard drainage to soakaways'],
      recs: ['Extend 10 m buffer on west ditch', 'Add small wetland at outfall'] },
    { key: 'Manure Practice & Timing', weight: 15, score: 88,
      explain: 'Adequate storage; timing aligned to growth.',
      reasons: ['6+ months storage', 'Trailing shoe', 'Rain avoidance rules'],
      recs: ['Trial inhibitor early spring', 'Log % area per pass'] },
  ],

  // 020 — strong overall, a couple of soil/structure watchpoints
  X10AI_FT_020: [
    { key: 'Nutrient Balance', weight: 40, score: 90,
      explain: 'Whole-farm N aligns to offtake with small silage-field surplus.',
      reasons: ['Split N programme tuned to growth', 'Concentrates moderate', 'Silage block slightly above demand'],
      recs: ['Trim N on 2nd cut by ~10%', 'Expand clover to reduce purchased N'] },
    { key: 'Soil Condition', weight: 25, score: 86,
      explain: 'Soil pH in spec; P index mostly 2; compaction maps show localised pans.',
      reasons: ['Recent pH correction effective', 'Compaction noted on headlands', 'OM steady but not increasing'],
      recs: ['Shallow sward-lift on compacted headlands', 'Incorporate cover/leys on lighter soils'] },
    { key: 'Runoff Protection', weight: 20, score: 84,
      explain: 'Good buffers; a few short setbacks near drains.',
      reasons: ['Buffers present on main watercourses', 'Short (<5 m) setbacks on minor drains'],
      recs: ['Standardise to 10 m on risk edges', 'Add track grips to break flow to drains'] },
    { key: 'Manure Practice & Timing', weight: 15, score: 88,
      explain: 'Trailing shoe + spring-first strategy evident; storage secure.',
      reasons: ['5–6 months covered storage', 'Applications clustered to growth windows'],
      recs: ['Adopt weather API trigger for “no-spread” alerts', 'Expand low-emission kit to all tankers'] },
  ],

  // 050 — Ian Harley
  X10AI_FT_050: [
    { key: 'Nutrient Balance', weight: 40, score: 88,
      explain: 'Balance close to neutral year-on-year; minor surpluses corrected.',
      reasons: ['Tight fertiliser-to-grass matching', 'Export of surplus slurry to neighbour'],
      recs: ['Formalise annual nutrient export agreement', 'Increase clover in low-index blocks'] },
    { key: 'Soil Condition', weight: 25, score: 84,
      explain: 'pH mostly 6.2–6.6; P index mixed (1–2); OM improving slowly.',
      reasons: ['Targeted liming programme ongoing', 'OM rising on reseeded fields'],
      recs: ['Lift P index 1 parcels via FYM/targeted P', 'Continue OM tracking with loss-on-ignition'] },
    { key: 'Runoff Protection', weight: 20, score: 82,
      explain: 'Tracks improved; two gateways still risk points.',
      reasons: ['Stoned tracks reduce fines', 'Gateways slope toward ditch'],
      recs: ['Install sleeper bars at gateways', 'Regrade fall away from watercourse'] },
    { key: 'Manure Practice & Timing', weight: 15, score: 90,
      explain: 'Timing excellent; minimal autumn spreading; equipment modern.',
      reasons: ['Trailing shoe + flow meter', 'Rain forecasts used operationally'],
      recs: ['Add soil moisture threshold (field sensors) to go/no-go', 'Share SOP with neighbours (peer standard)'] },
  ],

  // 010 — Steve Dakin
  X10AI_FT_010: [
    { key: 'Nutrient Balance', weight: 40, score: 80,
      explain: 'Slight N surplus on high-yield paddocks; scope to shave inputs.',
      reasons: ['Fertiliser plan conservative for yield risk', 'Silage block residual N not fully credited'],
      recs: ['Credit residual N post-cut', 'Trial 5–10% N reduction on two paddocks'] },
    { key: 'Soil Condition', weight: 25, score: 79,
      explain: 'pH ok; some subsoil compaction; OM variable.',
      reasons: ['Headland compaction from turning', 'Low OM on sandy ridge'],
      recs: ['Targeted sward lifting after dry spell', 'Overseed with multispecies to raise OM'] },
    { key: 'Runoff Protection', weight: 20, score: 78,
      explain: 'Buffers present but narrow along minor drains; a few pinch points.',
      reasons: ['3–5 m buffers in places', 'Two culverts concentrate flow'],
      recs: ['Standardise to 10 m on risk edges', 'Add swale upstream of culverts'] },
    { key: 'Manure Practice & Timing', weight: 15, score: 76,
      explain: 'Storage adequate; some late-season applications under wet ground.',
      reasons: ['Operational pressure pre-closed period', 'Weather windows missed in Sept'],
      recs: ['Increase buffer storage (contracted) by 3–4 weeks', 'Adopt “traffic-light” ground condition rule'] },
  ],

  // 060 — Melvin Jones (UPDATED from latest RiverSafe assessment)
  X10AI_FT_060: [
    { key: 'Nutrient Balance', weight: 40, score: 67,
      explain: 'Whole-farm N and P balance shows a moderate surplus versus crop offtake, with improved targeting compared with the previous season.',
      reasons: [
        'Total N applied (mineral plus organic) is approximately 24% above RB209 crop demand.',
        'Three fields receive more than 170 kg organic N/ha, covering about 12% of the farmed area.',
        'The remainder of the platform is broadly in line with RB209 recommendations, with mineral N and slurry increasingly targeted to higher demand fields.'
      ],
      recs: [
        'Reduce total N applications by around 15–20% on the three highest-loaded fields, primarily by cutting mineral N where organic N is already high.',
        'Bring all parcels back within the 170 kg organic N/ha NVZ limit, using export of slurry/digestate or re-allocation to lower-loaded fields where appropriate.',
        'Continue to use x10AI FarmTwin to align fertiliser plans with measured grass growth and crop offtake, so that N surpluses remain visible and can be corrected in-season.'
      ]
    },
    { key: 'Soil Condition', weight: 25, score: 78,
      explain: 'Soil P indices are low and organic matter is strong across most fields; pH is slightly below target on several parcels.',
      reasons: [
        'Updated soil tests show P Index 0–1 across sampled fields, with no Index 2 or 3, reducing P-loss risk to water.',
        'Soil organic matter is generally high (around 8–12% on most fields), supporting structure, infiltration and biological activity.',
        'Several fields have pH below 6.0, which can constrain yield and nutrient use efficiency if not addressed.'
      ],
      recs: [
        'Apply targeted lime to fields with pH <6.0 to bring them toward 6.0–6.2 for grassland.',
        'Maintain FYM and slurry inputs on low-P fields, while avoiding build-up that would move them rapidly toward Index 2.',
        'Re-test a representative sample of fields every 4–5 years to confirm that P indices remain low and organic matter is maintained or improved.'
      ]
    },
    { key: 'Runoff Protection', weight: 20, score: 67,
      explain: 'High grassland cover and strong boundary features reduce runoff risk, with localised issues around buffers and track drainage.',
      reasons: [
        'Drone-derived land classification shows 71% of the holding as farmed grassland, with only 7% arable, limiting bare-soil exposure.',
        'Around 21% of the mapped area is in hedgerows, trees and unfarmed boundaries, providing good interception and roughness.',
        'Residual risk remains where setback distances beside watercourses are <10 m and where tracks or gateways can concentrate flow toward drains or ditches.'
      ],
      recs: [
        'Extend buffers along higher-risk watercourse edges to at least 10 m where practical, prioritising fields with higher N and P applications.',
        'Break up flow paths on tracks and at gateways, for example with grips, cross-drains or stone to slow and disperse dirty water.',
        'Use the FarmTwin land-use layer to identify and monitor any future conversion of grass or semi-natural cover to higher-risk uses.'
      ]
    },
    { key: 'Manure Practice & Timing', weight: 15, score: 74,
      explain: 'Spreading dates and targeting are generally good; the main issue is application rate on a small number of fields.',
      reasons: [
        'Manure diary entries show applications mainly in April and July, aligned with NVZ open periods and away from the highest runoff risk windows.',
        'Organic manures are applied to around 44% of the farmed area, indicating deliberate targeting rather than blanket spreading.',
        'Three fields exceed 170 kg organic N/ha over the rolling year, which is above the NVZ limit and raises localised leaching and runoff risk.'
      ],
      recs: [
        'Re-balance organic N applications to ensure no field exceeds 170 kg organic N/ha in any 12-month period.',
        'Use FarmTwin to match slurry and FYM applications with grass growth forecasts and soil indices, prioritising lower-index, lower-loading fields.',
        'Continue to keep accurate digital records of all applications so that compliance and improvement trends can be demonstrated year-on-year.'
      ]
    },
  ],

  // 030 — Apley Farms
  X10AI_FT_030: [
    { key: 'Nutrient Balance', weight: 40, score: 58,
      explain: 'Notable surplus on whole-farm balance; fertiliser not fully offset by manure credits.',
      reasons: ['Limited use of RB209 manure credits', 'Concentrate inputs not reflected in plan'],
      recs: ['Adopt full manure N/P credits per RB209', 'Rebase fertiliser by paddock yield maps'] },
    { key: 'Soil Condition', weight: 25, score: 57,
      explain: 'Low pH patches; P index 3 common on silage platforms; OM below target in places.',
      reasons: ['Under-liming history', 'Legacy P from past applications'],
      recs: ['Two-year liming plan to reach pH 6.2–6.5', 'Suspend P on Index 3', 'Introduce ley/cover to lift OM'] },
    { key: 'Runoff Protection', weight: 20, score: 54,
      explain: 'Narrow/no buffers on multiple drains; yard runoff path to open ditch.',
      reasons: ['Buffers <3 m common', 'Hardstanding drains to ditch without intercept'],
      recs: ['10 m buffers on all risk edges', 'Install settlement/wetland cell at yard outlet'] },
    { key: 'Manure Practice & Timing', weight: 15, score: 52,
      explain: 'Timing frequently weather-driven; storage tight; applications close to rainfall.',
      reasons: ['Insufficient covered storage', 'Reactive rather than planned spreading'],
      recs: ['Secure +6 weeks storage capacity', 'Introduce “no-spread” rule (24–48 h pre-rain) and spring-first policy'] },
  ],
}

// Safety fallback (unused now that all are populated)
export const TEMPLATE_COMPONENTS = [
  { key: 'Nutrient Balance', weight: 40, score: 80, explain: 'Template – edit per farm.', reasons: ['Reason 1','Reason 2'], recs: ['Action 1','Action 2'] },
  { key: 'Soil Condition', weight: 25, score: 78, explain: 'Template – edit per farm.', reasons: ['Reason 1','Reason 2'], recs: ['Action 1','Action 2'] },
  { key: 'Runoff Protection', weight: 20, score: 75, explain: 'Template – edit per farm.', reasons: ['Reason 1','Reason 2'], recs: ['Action 1','Action 2'] },
  { key: 'Manure Practice & Timing', weight: 15, score: 76, explain: 'Template – edit per farm.', reasons: ['Reason 1','Reason 2'], recs: ['Action 1','Action 2'] },
]
